/**
 * Shared demographic catalogues for client and server.
 * Used to define canonical options for Demographics fields going forward (Te Kohinga Clients & Gambling Reporting).
 *
 * Bucket aliases are matched case-insensitively, with diacritics stripped, with
 * Stats NZ classification codes (e.g. "[0502]") removed, and with all
 * non-letters collapsed away. So "  Ngāi Tahu [1109] " → "ngaitahu".
 *
 * Two sentinel aliases discriminate the two kinds of catch-all:
 *   - EMPTY     matches null / undefined / empty-string input ("not provided")
 *   - UNMATCHED matches any non-empty value that didn't match another alias
 *               ("real value but not one we bucket explicitly")
 *
 * Sections list whichever sentinel applies. A section may use both (one for
 * "no data", one for "outside the tracked set"), just one, or neither.
 *
 * "Unknown" is filtered from drop-downs but appears in reports as a bucket.
 */

const EMPTY = Symbol("empty");
const UNMATCHED = Symbol("unmatched");

const GENDER_BUCKETS = {
  "Female": ["Woman", "F-Female"],
  "Male": ["Man"],
  "Gender diverse": ["Non-binary", "Other"],
  "Declined to provide": ["Chose not to answer"],
  "Unknown": [EMPTY, UNMATCHED],
};

const ETHNICITY_BUCKETS = {
  "Māori": ["NZ Maori"],
  "New Zealand European / Pākehā": [
    "NZ European/Pakeha", "NZ European", "Pakeha",
    "British /Irish", "United Kingdom/British",
  ],
  "Samoan": [],
  "Cook Island Māori": ["Cook Islands Maori", "Cook Is Maori", "Cook Island"],
  "Tongan": [],
  "Niuean": [],
  "Chinese": [],
  "Indian": [
    "Pakistani", "Bangladeshi", "Sri Lankan", "Nepalese", "Bhutanese",
    "Fijian Indian",
  ],
  "Other Pacific Peoples": [
    "Other Pacific Island", "Pacific Island", "Pacific Islander",
    "Pasifika", "Pacifika", "Pasifika - not further defined",
    "Papua New Guinea",
  ],
  "Other Asian": [
    "Asian",
    "Vietnamese", "Cambodian", "Indonesian", "Malaysian", "Thai",
    "Japanese", "Korean", "Filipino",
    "Southeast Asian", "South East Asian - Other", "Other Southeast Asian",
    "Afghan", "Pashtun", "Hazara", "Fars", "Kyrgyz",
    "Eurasian",
  ],
  "Middle Eastern": [
    "Arab", "Arabian", "Iranian", "Iraq", "Syrian", "Saudi", "Jordanian",
    "Egyptian", "Moroccan", "Algerian", "Turkish", "Palestinian",
    "Lor",
  ],
  "Latin American": [
    "Hispanic", "Latin American/Hispanic", "Latin American - Other",
    "Argentinian", "South American",
  ],
  "African": [
    "Nigerian", "Kenyian", "Sudanese", "South African", "Mauritius",
    "Ethiopian", "Eritrean", "Somali",
    "Tigrinya", "Tigrina", "Oromo", "Saho",
    "Reer Hamar (Fipe Clan)", "Jaaji", "Kunama", "Ashraf",
  ],
  "Other": [
    UNMATCHED,
    "Other European", "European", "European - not further defined",
    "Aboriginal", "Tokelauan", "Fijian",
    "Polish", "Dutch", "German", "Greek", "Italian", "Russian",
    "North American", "Canadian", "Australian", "Indigenous Australian",
    "Non NZ European",
    "Other Ethnicity",
  ],
  "Declined to provide": ["Chose not to answer", "Not Specified"],
  "Unknown": [EMPTY],
};

const IWI_OPTIONS = [
  "Ahuriri",
  "Heretaunga Tamatea",
  "Kāti Māmoe",
  "Maungaharuru Tangitū",
  "Moriori",
  "Muaūpoko",
  "Ngā Pōtiki",
  "Ngā Rauru",
  "Ngā Wairiki Ngāti Apa",
  "Ngāi Tahu",
  "Ngāi Tai ki Tāmaki",
  "Ngāi Takoto",
  "Ngāi Tāmanuhiri",
  "Ngāi Tāhuhu",
  "Ngāi Te Ohuake",
  "Ngāi Te Rangi",
  "Ngāitai",
  "Ngāpuhi",
  "Ngāruahine",
  "Ngāti Apa ki te Rā Tō",
  "Ngāti Awa",
  "Ngāti Hako",
  "Ngāti Hāua",
  "Ngāti Hauā",
  "Ngāti Hauiti",
  "Ngāti Hei",
  "Ngāti Hikairo",
  "Ngāti Hinerangi",
  "Ngāti Hineuru",
  "Ngāti Hine",
  "Ngāti Kahu",
  "Ngāti Kahu ki Whangaroa",
  "Ngāti Kahungunu",
  "Ngāti Kahungunu ki Heretaunga",
  "Ngāti Kahungunu ki Tamakinui a Rua",
  "Ngāti Kahungunu ki Tamatea",
  "Ngāti Kahungunu ki Te Whanganui-a-Orotu",
  "Ngāti Kahungunu ki Te Wairoa",
  "Ngāti Kahungunu ki Wairarapa",
  "Ngāti Kauwhata",
  "Ngāti Kea / Ngāti Tuarā",
  "Ngāti Koata",
  "Ngāti Korokī Kahukura",
  "Ngāti Kuia",
  "Ngāti Kurī",
  "Ngāti Mākino",
  "Ngāti Manawa",
  "Ngāti Maniapoto",
  "Ngāti Manuhiri",
  "Ngāti Maru (Hauraki)",
  "Ngāti Maru (Taranaki)",
  "Ngāti Mutunga",
  "Ngāti Mutunga o Wharekauri",
  "Ngāti Pāhauwera",
  "Ngāti Pāoa",
  "Ngāti Pikiao",
  "Ngāti Porou",
  "Ngāti Porou ki Hauraki",
  "Ngāti Pūkenga",
  "Ngāti Pūkenga ki Waiau",
  "Ngāti Rāhiri Tumutumu",
  "Ngāti Rākaipaaka",
  "Ngāti Rangi",
  "Ngāti Ranginui",
  "Ngāti Rangiteaorere",
  "Ngāti Rangitihi",
  "Ngāti Rangiwewehi",
  "Ngāti Rārua",
  "Ngāti Raukawa",
  "Ngāti Raukawa (Waikato)",
  "Ngāti Raukawa ki te Tonga",
  "Ngāti Rehua",
  "Ngāti Rongomai",
  "Ngāti Ruanui",
  "Ngāti Ruapani",
  "Ngāti Tahu – Ngāti Whaoa",
  "Ngāti Tama",
  "Ngāti Tama ki Te Tau Ihu",
  "Ngāti Tama ki Te Upoko o Te Ika",
  "Ngāti Tama ki Taranaki",
  "Ngāti Tama ki Te Waipounamu",
  "Ngāti Tama Kopiri",
  "Ngāti Tamaoho",
  "Ngāti Tamakōpiri",
  "Ngāti Tamaterā",
  "Ngāti Tara Tokanui",
  "Ngāti Tarāwhai",
  "Ngāti Te Ata",
  "Ngāti Tiipa",
  "Ngāti Toarangatira",
  "Ngāti Toarangatira ki Te Waipounamu",
  "Ngāti Toarangatira ki Te Whanganui-a-Tara",
  "Ngāti Tukorehe",
  "Ngāti Tūmatakōkiri",
  "Ngāti Tūrangitukua",
  "Ngāti Tūwharetoa",
  "Ngāti Wai",
  "Ngāti Whakahemo",
  "Ngāti Whakaue",
  "Ngāti Whanaunga",
  "Ngāti Whare",
  "Ngāti Whātua",
  "Ngāti Whātua o Kaipara",
  "Ngāti Whātua Ōrākei",
  "Ngāti Whitikaupeka",
  "Pakakohi",
  "Rangitāne",
  "Rangitāne o Manawatū",
  "Rangitāne o Tamaki nui a Rua",
  "Rangitāne o Te Matau-a-Māui",
  "Rangitāne o Wairau",
  "Rereahu",
  "Rongomaiwahine",
  "Rongowhakaata",
  "Tamahaki",
  "Tamakana",
  "Tangāhoe",
  "Tapuika",
  "Taranaki",
  "Te Aitanga a Hauiti",
  "Te Aitanga-a-Māhaki",
  "Te Ākitai Waiohua",
  "Te Āti Haunui-a-Pāpārangi",
  "Te Atiawa",
  "Te Atiawa ki Whakarongotai",
  "Te Atiawa o Te Whanganui-a-Tara",
  "Te Atiawa Taranaki",
  "Te Ātiawa o Te Waka-a-Māui",
  "Te Aupōuri",
  "Te Hapū-oneone",
  "Te Hika o Papauma",
  "Te Kawerau ā Maki",
  "Te Korowai o Wainuiārua",
  "Te Paatu",
  "Te Patukirikiri",
  "Te Rarawa",
  "Te Roroa",
  "Te Ūpokorehe",
  "Te Uri-o-Hau",
  "Te Wairoa",
  "Te Whānau-ā-Apanui",
  "Tūhoe",
  "Tūhourangi",
  "Uenuku",
  "Uenuku-Kōpako",
  "Waikato",
  "Waitaha (Bay of Plenty)",
  "Waitaha (Te Waipounamu)",
  "Whakatōhea",
];

const IWI_REPORT_BUCKETS = {
  "Ngāpuhi": [
    "Nga Puhi",
    "Ngāpuhi ki Whaingaroa-Ngāti Kahu ki Whaingaroa",
    "Ngapuhi ki Whaingaroa-Ngati Kahu ki Whaingaroa",
  ],
  "Tūhoe": ["Ngai Tuhoe / Tuhoe", "Ngāi Tūhoe"],
  "Ngāti Porou": [
    "Ngāti Porou ki Harataunga ki Mataora",
    "Ngāti Porou ki Hauraki",
  ],
  "Tainui": [
    "Waikato",
    "Tainui, iwi not named",
    "Aotea, iwi not named",
  ],
  "Ngāi Tahu": [
    "Kai Tahu", "Kāi Tahu",
    "Ngāi Tahu / Kai Tahu",
    "Ngai Tahu specific hapu not known",
    "Ngai Tahu primarily Koukourarta (Port Levy)",
    "Ngai Tahu primarily Rapaki",
    "Poutini",
  ],
  "Ngāti Tūwharetoa": [
    "Ngāti Tūwharetoa (ki Taupo)",
    "Ngāti Tūwharetoa ki Kawerau",
    "Ngati Tuwharetoa, region not known",
  ],
  "Ngāti Maniapoto": [],
  "Ngāti Raukawa": [
    "Ngati Ruakawa", "Ngāti Ruakawa",
    "Ngāti Raukawa (Horowhenua/Manawatu)",
    "Ngāti Raukawa (Waikato)",
    "Ngāti Raukawa ki te Tonga",
    "Ngati Raukawa, region not known",
    "Raukawa (Waikato)",
  ],
  "Ngāti Kahungunu": [
    "Ngāti Kahungunu ki Heretaunga",
    "Ngāti Kahungunu ki Tamakinui a Rua",
    "Ngāti Kahungunu ki Tamatea",
    "Ngāti Kahungunu ki Te Whanganui-a-Orotu",
    "Ngāti Kahungunu ki Te Wairoa",
    "Ngati Kahungunu ki Wairoa",
    "Ngāti Kahungunu ki Wairarapa",
    "Ngati Kahungunu, region not known",
  ],
  "Te Arawa": [
    "Te Arawa, iwi not named",
    "Te Arawa/Taupo Region, Iwi not named",
    "Ngāti Pikiao (Te Arawa)",
    "Ngāti Whakaue (Te Arawa)",
    "Ngāti Rangitihi (Te Arawa)",
    "Ngāti Tahu-Ngāti Whaoa (Te Arawa)",
    "Ngāti Tahu – Ngāti Whaoa",
  ],
  "Ngāti Awa": [],
  "Te Rarawa": [],
  "Te Aupōuri": ["Te Aupouri"],
  "Te Atiawa": [
    "Te Ati Awa", "Te Ātiawa",
    "Te Atiawa (Taranaki)",
    "Te Atiawa, region not known",
    "Te Atiawa (Te Waipounamu/South Island)",
  ],
  "Te Whānau-ā-Apanui": [
    "Te Whanau-a-Apanui", "Whanau-a-Apanui",
  ],
  "Whakatōhea": ["Whakatohea"],
  "Rongowhakaata": [],
  "Te Aitanga-a-Māhaki": ["Te Aitanga-a-Mahaki", "Te Aitanga a Hauiti"],
  "Ngāti Ranginui": [],
  "Muaūpoko": ["Muaupoko"],
  "Other": [UNMATCHED],
  "Unknown": [
    EMPTY,
    "Not specified", "Not Stated", "Not Applicable",
    "a non Maori client/person",
    "a Not known iwi only use if not known",
    "Hapu Affiliated to More Than One Iwi",
  ],
};

const isEmptyBucket = (aliases) => aliases.includes(EMPTY);

const optionsFromBuckets = (buckets) =>
  Object.entries(buckets)
    .filter(([, aliases]) => !isEmptyBucket(aliases))
    .map(([name]) => name);

const GENDER_OPTIONS = optionsFromBuckets(GENDER_BUCKETS);
const ETHNICITY_OPTIONS = optionsFromBuckets(ETHNICITY_BUCKETS);

module.exports = {
  EMPTY,
  UNMATCHED,
  GENDER_BUCKETS,
  ETHNICITY_BUCKETS,
  IWI_REPORT_BUCKETS,
  GENDER_OPTIONS,
  ETHNICITY_OPTIONS,
  IWI_OPTIONS,
};
